/********************************************************************************
 * Copyright (c) 2026 EclipseSource and others.
 *
 * This program and the accompanying materials are made available under the
 * terms of the MIT License which is available in the project root.
 *
 * SPDX-License-Identifier: MIT
 ********************************************************************************/

/**
 * The properties panel's own user-facing messages.
 *
 * An adopter declares its messages with the same `defineMessage` the framework
 * uses, and that is the whole of the mechanism: a stable code beside an English
 * default, resolved at the raise site and rendered by whoever knows the reading
 * user's locale.
 *
 * **The `hydranium/` code namespace is reserved for the framework**, whose
 * per-package tests assert that every code in a framework barrel names the
 * package it is declared in. An adopter therefore prefixes with its OWN name —
 * here `order-flow/<area>/<name>`, segments limited to `[a-z0-9-]`. Sharing the
 * framework's prefix would put two owners in one keyspace, and a rename on
 * either side would silently shadow the other's catalogue entry.
 *
 * **Declared here rather than per host, because the panel is one surface.** The
 * Theia widget, the VS Code webview and the browser page mount the same
 * `PropertiesForm` over the same `PropertiesDocuments` and differ only in how
 * they reach the data head, so a failure has to read identically in each. A
 * code per host would make a translator maintain an entry per host for one
 * sentence and let them drift.
 *
 * **Complete sentences with the detail interpolated**, matching what the
 * framework's own messages carry. A bare fragment has to be nested in a sentence
 * its owner did not write, so no translator controls the whole and the
 * composition cannot be made to read correctly in every language.
 * `describeFailure` is what fills `{detail}`: a technical error string is
 * not itself translatable text, so it travels as a parameter rather than needing
 * a code of its own, and an error that has a code is rendered by it.
 *
 * **The panel's own LABELS carry codes as well as its failures, and the two
 * differ in who renders them.** A failure is resolved at the raise site and
 * handed to whoever owns the surface, that being the tier which knows the
 * reading user's locale. A label has no raise site — `PropertiesForm` draws it —
 * so the form is handed a renderer instead. Leaving the labels as literals is
 * what makes a panel read half-translated, and the untranslated half is the half
 * a reader meets on an ordinary document rather than on a broken one.
 */

import { defineMessage } from '@hydranium/protocol';

export const PROPERTIES_OPEN_FAILED = defineMessage(
   'order-flow/properties/open-failed',
   'Order Flow properties: could not open {uri}. {detail}'
);

export const PROPERTIES_WRITE_FAILED = defineMessage(
   'order-flow/properties/write-failed',
   "Order Flow properties: could not save '{field}' of {uri}. {detail}"
);

export const PROPERTIES_NO_DOCUMENT = defineMessage('order-flow/properties/no-document', 'No Order Flow document selected');

export const PROPERTIES_NO_FIELDS = defineMessage('order-flow/properties/no-fields', 'This document root has no editable text properties.');

export const PROPERTIES_LOADING = defineMessage('order-flow/properties/loading', 'Loading…');

export const PROPERTIES_APPLY_HINT = defineMessage('order-flow/properties/apply-hint', 'Press Enter to apply');

export const PROPERTIES_UNSAVED = defineMessage('order-flow/properties/unsaved', 'Unsaved changes');

export const PROPERTIES_DISCONNECTED = defineMessage(
   'order-flow/properties/disconnected',
   'The data server connection closed. Reopen the panel to reconnect.'
);

/**
 * Each of these says what became of the write AND whose value the box now holds,
 * because the two come apart: a merge kept the edit, while a conflict or an
 * unavailable document leaves a draft nothing has accepted. A
 * translation that shortens one to "saved" or "not saved" leaves a reader unable
 * to tell whether what is in front of them is their own.
 */
export const PROPERTIES_WRITE_MERGED = defineMessage(
   'order-flow/properties/write-merged',
   'Saved. Someone else had edited another field; both changes were kept.'
);

export const PROPERTIES_WRITE_CONFLICT = defineMessage(
   'order-flow/properties/write-conflict',
   'Not saved — someone else changed this field first. Your draft has been kept.'
);

export const PROPERTIES_WRITE_RAN_OUT = defineMessage(
   'order-flow/properties/write-ran-out',
   'Not saved — the document kept changing while this was written. Try again.'
);

export const PROPERTIES_WRITE_UNAVAILABLE = defineMessage(
   'order-flow/properties/write-unavailable',
   'Not saved — the document was no longer open when the change was to be written.'
);

/** An edit of the previous document that lost, reported through the error sink, since the panel shows the next one. */
export const PROPERTIES_PREVIOUS_WRITE_CONFLICT = defineMessage(
   'order-flow/properties/previous-write-conflict',
   "Order Flow properties: '{field}' of {uri} was not saved. Someone else changed it first."
);

export const PROPERTIES_PREVIOUS_WRITE_RAN_OUT = defineMessage(
   'order-flow/properties/previous-write-ran-out',
   "Order Flow properties: '{field}' of {uri} was not saved. The document kept changing while it was written."
);

export const PROPERTIES_PREVIOUS_WRITE_UNAVAILABLE = defineMessage(
   'order-flow/properties/previous-write-unavailable',
   "Order Flow properties: '{field}' of {uri} was not saved. The document was no longer open when the change was to be written."
);
